import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  CardBodyComponent,
  CardComponent,
  GroundworksTableComponent,
  GroundworksTableData,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
  TermValueComponent,
  TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { catchError, EMPTY } from 'rxjs';
import { ServicePlanProductConstants } from '../../constants/service-plan-product.constants';
import { JobProductInfo, ProductWithPricingInfo, CreditCandidateInfo, CreditCandidateGroup } from '../../sdk/sdk';
import { ChangeOrderService } from '../../services/change-order.service';
import { ProductService } from '../../services/product.service';
import { UserService } from '../../services/user.service';
import { prorateAmount, parseMoney } from '../../utils/money';
import { isValidDescopeChange, isMoneyOnlyDescopeEdit } from '../../utils/descope-change';

// Membership set of service-plan product ids, derived from the auto-generated
// ServicePlanProductConstants. Used to enforce service-plan rules: clamp
// quantities to 1 and allow at most one service plan per change order.
const SERVICE_PLAN_PRODUCT_IDS = new Set<string>(Object.values(ServicePlanProductConstants));

export interface ChangeOrderFormData {
  jobId: string;
  changeOrderDate: Date;
  currentJobTotal: number;
  netChangeAmount: number;
  newJobTotal: number;
  soldBy: CreditCandidateInfo | null;
  upsellItems: UpsellProduct[];
  descopeItems: DescopeProduct[];
  taxes: TaxEditLine[];
}

interface UpsellProduct {
  productWithPricing: ProductWithPricingInfo;
  quantity: number;
  netAmount: number;
}

export interface DescopeProduct {
  productWithPricing: JobProductInfo;
  quantity: number;
  originalQuantity: number;
  netAmount: number;
  originalNetAmount: number;
  isRemoved?: boolean;
}

export interface TaxEditLine {
  taxTypeId: string;
  taxTypeName?: string;
  amount: number;
  originalAmount: number;
}

const ITEMS_AND_TAXES_MIXED_ERROR =
  'Upsell/Descope and Tax edits are mutually exclusive per change order. Please complete one at a time.';

const DESCOPE_MONEY_ONLY_WARNING =
  'To descope a product you must reduce its quantity or remove it. Changing only the amount is not allowed — adjust the quantity, remove the product, or restore the original amount to continue.';

@Component({
  selector: 'app-change-order-modal',
  imports: [
    CardComponent,
    CardBodyComponent,
    NgSelectComponent,
    FormsModule,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    TermValueComponent,
    GroundworksTableComponent,
  ],
  templateUrl: './change-order-modal.component.html',
  styleUrls: ['./change-order-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeOrderModalComponent implements OnInit {
  private readonly changeOrderService = inject(ChangeOrderService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly destroyRef = inject(DestroyRef);
  private readonly productService = inject(ProductService);
  private readonly userService = inject(UserService);

  // MOD-4240: one idempotency key per modal instance, generated at construction so it stays stable
  // across the user's manual retries within this instance (a retry after a failed submit is the same
  // instance) but is fresh when the modal is reopened (a new instance). Never regenerate inside
  // submit() — that mints a key per click and defeats the legacy-pushdown dedup.
  private readonly idempotencyKey = crypto.randomUUID();

  readonly jobNumber = signal('');
  readonly jobId = signal('');
  readonly currentJobTotal = signal(0);
  readonly branchId = signal('');
  readonly postalCode = signal('');

  readonly upsellExpanded = signal(false);
  readonly descopeExpanded = signal(false);
  readonly taxesExpanded = signal(false);
  readonly products = signal<ProductWithPricingInfo[]>([]);
  readonly selectedProduct = signal<ProductWithPricingInfo | null>(null);
  readonly upsellProducts = signal<UpsellProduct[]>([]);
  readonly descopeItems = signal<DescopeProduct[]>([]);
  readonly taxes = signal<TaxEditLine[]>([]);
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly removedDescopeNetTotal = computed(() =>
    this.descopeItems()
      .filter((item) => item.isRemoved)
      .reduce((sum, item) => sum + item.originalNetAmount, 0),
  );
  readonly salespeople = signal<CreditCandidateInfo[]>([]);
  readonly selectedSalesperson = signal<CreditCandidateInfo | null>(null);

  // MOD-3920 #4: the "Credit To" picker can expand from the by-job list to the full active
  // company directory. Toggling swaps the picker's item source and drops grouping.
  readonly showAllCompany = signal(false);
  readonly allCompanySalespeople = signal<CreditCandidateInfo[]>([]);
  readonly displayedSalespeople = computed(() =>
    this.showAllCompany() ? this.allCompanySalespeople() : this.salespeople(),
  );

  // By-job picker headers: the server's three ranking groups collapse to two display headers.
  // Promoted and Connection both surface as "Recommended" (server keeps them in rank order, so
  // promoted still precedes connection); Remaining is "Branch Employees". Bound as a stable
  // ng-select groupBy function so the header text comes straight from this map.
  readonly creditGroupLabel = (candidate: CreditCandidateInfo): string =>
    candidate.group === CreditCandidateGroup.Remaining ? 'Branch Employees' : 'Recommended';

  readonly hasDescopeChanges = computed(
    () =>
      this.descopeItems().some(
        (item) =>
          item.isRemoved ||
          item.netAmount !== item.originalNetAmount ||
          item.quantity !== item.originalQuantity,
      ),
  );

  readonly hasUpsellProducts = computed(() => this.upsellProducts().length > 0);

  readonly hasTaxChanges = computed(() =>
    this.taxes().some((t) => t.amount !== t.originalAmount),
  );

  // A money-only descope edit (amount changed, quantity untouched, not removed) is no longer a
  // valid change. Its presence blocks submission entirely — matching the backend, which rejects
  // the whole change order — so a typed amount can never be silently dropped.
  readonly descopeMoneyOnlyExists = computed(() =>
    this.descopeItems().some((item) => isMoneyOnlyDescopeEdit(item)),
  );

  readonly descopeMoneyOnlyWarning = computed(() =>
    this.descopeMoneyOnlyExists() ? DESCOPE_MONEY_ONLY_WARNING : null,
  );

  readonly canSubmit = computed(() => {
    const hasUpsell = this.upsellProducts().length > 0;
    if (hasUpsell && this.selectedSalesperson() === null) return false;

    return (
      (hasUpsell ||
        this.removedDescopeNetTotal() > 0 ||
        this.descopeItems().some((item) => !item.isRemoved && isValidDescopeChange(item)) ||
        this.hasTaxChanges()) &&
      !this.descopeMoneyOnlyExists()
    );
  });

  readonly upsellNetTotal = computed(() =>
    this.upsellProducts().reduce((sum, item) => sum + item.netAmount, 0),
  );

  readonly netChangeAmount = computed(
    () =>
      this.upsellNetTotal() +
      this.descopeItems().reduce(
        (sum, item) =>
          item.isRemoved ? sum - item.originalNetAmount : sum + (item.netAmount - item.originalNetAmount),
        0,
      ),
  );

  readonly newJobTotal = computed(() => this.currentJobTotal() + this.netChangeAmount());

  readonly changeOrderDate = new Date();

  readonly summaryItems = computed<TermValueItem[]>(() => [
    {
      term: 'Change Order Date',
      value: this.changeOrderDate,
      type: 'date' as const,
      format: 'MM/dd/yyyy',
    },
    {
      term: 'Current Job Total',
      value: this.currentJobTotal(),
      type: 'currency' as const,
      variant: 'subtotal' as const,
    },
    {
      term: 'Net Change Amount',
      value: this.netChangeAmount(),
      type: 'currency' as const,
    },
    {
      term: 'New Job Total',
      value: this.newJobTotal(),
      type: 'currency' as const,
      variant: 'total' as const,
    },
  ]);

  readonly footerActions = computed<ModalFooterAction[]>(() => [
    {
      label: 'Complete Change Order',
      type: 'primary',
      action: () => this.submit(),
      disabled: () => !this.canSubmit() || this.loading(),
      loading: () => this.loading(),
    },
  ]);
  readonly upsellTableData = computed<GroundworksTableData>(() => ({
    columns: [
      { label: 'Product Name', width: '25%' },
      { label: 'Units', width: '35%' },
      { label: 'Gross', dataType: 'currency' as const },
      { label: 'Net', width: '50%' },
      { label: 'Edit', dataType: 'action-menu' as const },
    ],
    rows: this.upsellProducts().map((product, i) => ({
      type: 'data' as const,
      cells: [
        { value: product.productWithPricing.name ?? '' },
        {
          value: '',
          input: {
            type: 'number' as const,
            defaultValue: product.quantity,
            ariaLabel: 'Quantity',
            suffix: product.productWithPricing.unitOfMeasure ?? '',
            onChange: (val: string | number) => this.updateUpsellItemQuantity(i, +val),
            rules: { min: 1, ...(SERVICE_PLAN_PRODUCT_IDS.has(product.productWithPricing.id) && { max: 1 }) },
          },
        },
        { value: (product.productWithPricing.listPrice ?? 0) * product.quantity },
        {
          value: '',
          input: {
            type: 'currency' as const,
            defaultValue: (Math.round(product.netAmount * 100) / 100).toFixed(2),
            ariaLabel: 'Net amount',
            onChange: (val: string | number) => this.onUpsellNetAmountFromTable(i, val),
            rules: { decimalPlaces: 2 },
          },
        },
        {
          value: '',
          actionButton: {
            icon: 'bi bi-trash',
            ariaLabel: 'Remove item',
            variant: 'danger' as const,
            action: () => this.removeUpsellItem(i),
          },
        },
      ],
    })),
  }));

  readonly descopeTableData = computed<GroundworksTableData>(() => ({
    columns: [
      { label: 'Product Name', width: '25%' },
      { label: 'Units', width: '35%' },
      { label: 'Gross', dataType: 'currency' as const},
      { label: 'Net', width: '50%' },
      { label: 'Edit', dataType: 'action-menu' as const},
    ],
    rows: this.descopeItems().map((item, i) => ({
      type: 'data' as const,
      isMuted: item.isRemoved,
      cells: [
        {
          value: item.productWithPricing.name ?? '',
          ...(item.productWithPricing.notes?.trim() && {
            icon: {
              class: 'bi bi-sticky text-primary',
              tooltip: item.productWithPricing.notes.trim(),
              ariaLabel: `Notes for ${item.productWithPricing.name ?? 'product'}`,
              position: 'after' as const,
              tooltipPlacement: 'top' as const,
            },
          }),
        },
        {
          value: '',
          input: {
            type: 'number' as const,
            defaultValue: item.quantity,
            ariaLabel: 'Quantity',
            suffix: item.productWithPricing.unitOfMeasure ?? '',
            onChange: (val: string | number) =>
              this.onDescopeQuantityFromTable(i, +val, item.originalQuantity),
            rules: { min: 1, max: item.originalQuantity },
            disabled: item.isRemoved,
          },
        },
        { value: (item.productWithPricing.listPrice ?? 0) * item.quantity },
        {
          value: '',
          input: {
            type: 'currency' as const,
            defaultValue: (Math.round(item.netAmount * 100) / 100).toFixed(2),
            ariaLabel: 'Net amount',
            onChange: (val: string | number) =>
              this.onDescopeNetAmountFromTable(i, val, item.originalNetAmount),
            rules: { min: 0, max: item.originalNetAmount, decimalPlaces: 2 },
            disabled: item.isRemoved,
          },
        },
        {
          value: '',
          actionButton: item.isRemoved
            ? {
                icon: 'bi bi-plus-circle-fill',
                ariaLabel: 'Restore item',
                variant: 'secondary' as const,
                action: () => this.restoreDescopeItem(i),
              }
            : {
                icon: 'bi bi-trash',
                ariaLabel: 'Remove item',
                variant: 'danger' as const,
                action: () => this.removeDescopeItem(i),
              },
        },
      ],
    })),
  }));

  readonly taxesTableData = computed<GroundworksTableData>(() => ({
    columns: [
      { label: 'Tax' },
      { label: 'Amount' },
    ],
    rows: this.taxes().map((tax, i) => ({
      type: 'data' as const,
      cells: [
        { value: tax.taxTypeName ?? tax.taxTypeId },
        {
          value: '',
          input: {
            type: 'currency' as const,
            defaultValue: (Math.round(tax.amount * 100) / 100).toFixed(2),
            ariaLabel: 'Tax amount',
            onChange: (val: string | number) => this.onTaxAmountFromTable(i, val),
            rules: { min: 0, decimalPlaces: 2 },
          },
        },
      ],
    })),
  }));

  changeOrderGrossTotal = signal(0);
  changeOrderNetTotal = signal(0);

  constructor() {
    effect(() => {
      const jobId = this.jobNumber();
      if (!jobId) return;
      this.productService
        .getProductsByJobId(jobId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((products) =>
          this.descopeItems.set(
            products.map((p) => ({
              productWithPricing: p,
              quantity: p.quantity ?? 0,
              originalQuantity: p.quantity ?? 0,
              netAmount: p.netAmount ?? 0,
              originalNetAmount: p.netAmount ?? 0,
              isRemoved: false,
            })),
          ),
        );
    });
  }

  ngOnInit(): void {
    const postalCode = this.postalCode();

    if (postalCode) {
      this.productService
        .getProductsForPostalCode(postalCode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((products) => this.products.set(products));
    }

    // The server returns credit candidates already ordered (Promoted -> Connection ->
    // Remaining, alphabetised within each group), so the dropdown binds the list as-is
    // and groups it via ng-select [groupBy].
    this.userService
      .getCreditCandidates(this.jobId(), this.branchId())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY),
      )
      .subscribe((candidates) => this.salespeople.set(candidates));
  }

  onSalespersonChange(userId: string): void {
    const user = this.displayedSalespeople().find((s) => s.id === userId) ?? null;
    this.selectedSalesperson.set(user);
  }

  loadAllCompany(): void {
    if (this.showAllCompany()) return;

    // Clear the current selection: a person picked from the by-job list is not guaranteed to
    // line up once the source swaps, so the user re-selects from the full directory.
    this.selectedSalesperson.set(null);
    this.userService
      .getAllCreditCandidates()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY),
      )
      .subscribe((candidates) => this.allCompanySalespeople.set(candidates));
    this.showAllCompany.set(true);
  }

  toggleUpsell(): void {
    this.upsellExpanded.update((v) => !v);
  }

  toggleDescope(): void {
    this.descopeExpanded.update((v) => !v);
  }

  toggleTaxes(): void {
    this.taxesExpanded.update((v) => !v);
  }

  updateTaxAmount(index: number, amount: number): void {
    this.taxes.update((items) =>
      items.map((item, i) => (i === index ? { ...item, amount } : item)),
    );
  }

  onTaxAmountFromTable(index: number, val: string | number): void {
    const value = typeof val === 'number' ? val : this.parseNetAmount(val);
    const clamped = Number.isNaN(value) ? 0 : Math.max(value, 0);
    this.updateTaxAmount(index, clamped);
  }

  addUpsellProduct(): void {
    const product = this.selectedProduct();
    if (!product) return;
    const alreadyAdded = this.upsellProducts().some(
      (item) => item.productWithPricing.id === product.id,
    );
    if (alreadyAdded) return;

    if (SERVICE_PLAN_PRODUCT_IDS.has(product.id)) {
      const hasExistingServicePlan = this.upsellProducts().some(
        (item) => SERVICE_PLAN_PRODUCT_IDS.has(item.productWithPricing.id),
      );
      if (hasExistingServicePlan) {
        this.errorMessage.set('Only one service plan is allowed per change order.');
        return;
      }
    }

    this.errorMessage.set(null);
    this.upsellProducts.update((items) => [
      ...items,
      { productWithPricing: product, quantity: 1, netAmount: product.listPrice },
    ]);
    this.selectedProduct.set(null);
  }

  removeUpsellItem(index: number): void {
    this.upsellProducts.update((items) => items.filter((_, i) => i !== index));
    this.errorMessage.set(null);
  }

  updateUpsellItemQuantity(index: number, quantity: number): void {
    this.upsellProducts.update((items) =>
      items.map((item, i) => {
        if (i !== index) return item;
        const clampedQuantity = SERVICE_PLAN_PRODUCT_IDS.has(item.productWithPricing.id)
          ? 1
          : quantity;
        return {
          ...item,
          quantity: clampedQuantity,
          netAmount: Math.round(item.productWithPricing.listPrice * clampedQuantity * 100) / 100,
        };
      }),
    );
  }

  updateUpsellItemNet(index: number, net: number): void {
    this.upsellProducts.update((items) =>
      items.map((item, i) => (i === index ? { ...item, netAmount: net } : item)),
    );
  }

  onUpsellNetChange(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseNetAmount(input.value);
    const clamped = Number.isNaN(value) ? 0 : Math.max(value, 0);
    input.value = String(clamped);
    this.updateUpsellItemNet(index, clamped);
  }

  onUpsellNetAmountFromTable(index: number, val: string | number): void {
    const value = typeof val === 'number' ? val : this.parseNetAmount(val);
    const clamped = Number.isNaN(value) ? 0 : Math.max(value, 0);
    this.updateUpsellItemNet(index, clamped);
  }

  onDescopeQuantityFromTable(index: number, val: number, originalQuantity: number): void {
    const clamped =
      Number.isNaN(val) || val > originalQuantity ? originalQuantity : Math.max(val, 1);
    this.updateDescopeItemQuantity(index, clamped);
  }

  onDescopeNetAmountFromTable(
    index: number,
    val: string | number,
    originalNetAmount: number,
  ): void {
    const value = typeof val === 'number' ? val : this.parseNetAmount(val);
    const clamped =
      Number.isNaN(value) || value > originalNetAmount ? originalNetAmount : Math.max(value, 0);
    this.updateDescopeItemNetAmount(index, clamped);
  }

  removeDescopeItem(index: number): void {
    this.descopeItems.update((items) =>
      items.map((item, i) => (i === index ? { ...item, isRemoved: true } : item)),
    );
  }

  restoreDescopeItem(index: number): void {
    this.descopeItems.update((items) =>
      items.map((item, i) => (i === index ? { ...item, isRemoved: false } : item)),
    );
  }

  onDescopeQuantityChange(event: Event, index: number, originalQuantity: number): void {
    const input = event.target as HTMLInputElement;
    const decimalValue = Number.parseInt(input.value, 10);
    const clamped =
      Number.isNaN(decimalValue) || decimalValue > originalQuantity
        ? originalQuantity
        : Math.max(decimalValue, 1);
    input.value = String(clamped);
    this.updateDescopeItemQuantity(index, clamped);
  }

  onDescopeNetAmountChange(event: Event, index: number, originalNetAmount: number): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseNetAmount(input.value);
    const clamped =
      Number.isNaN(value) || value > originalNetAmount ? originalNetAmount : Math.max(value, 0);
    input.value = String(clamped);
    this.updateDescopeItemNetAmount(index, clamped);
  }

  updateDescopeItemQuantity(index: number, quantity: number): void {
    this.descopeItems.update((items) =>
      items.map((item, i) =>
        i === index
          ? {
              ...item,
              quantity,
              netAmount: prorateAmount(item.originalNetAmount, item.originalQuantity, quantity),
            }
          : item,
      ),
    );
  }

  updateDescopeItemNetAmount(index: number, netAmount: number): void {
    this.descopeItems.update((items) =>
      items.map((item, i) => (i === index ? { ...item, netAmount } : item)),
    );
  }

  parseNetAmount(value: string): number {
    return parseMoney(value);
  }

  submit(): void {
    const hasItemChanges =
      this.upsellProducts().length > 0 ||
      this.removedDescopeNetTotal() > 0 ||
      this.descopeItems().some((item) => !item.isRemoved && isValidDescopeChange(item));

    if (hasItemChanges && this.hasTaxChanges()) {
      this.errorMessage.set(ITEMS_AND_TAXES_MIXED_ERROR);
      return;
    }

    const formData: ChangeOrderFormData = {
      jobId: this.jobNumber(),
      changeOrderDate: this.changeOrderDate,
      currentJobTotal: this.currentJobTotal(),
      netChangeAmount: this.netChangeAmount(),
      newJobTotal: this.newJobTotal(),
      soldBy: this.selectedSalesperson(),
      upsellItems: this.upsellProducts(),
      descopeItems: this.descopeItems().map((item) =>
        item.isRemoved ? { ...item, quantity: 0, netAmount: 0 } : item,
      ),
      taxes: this.taxes(),
    };

    this.loading.set(true);
    this.errorMessage.set(null);
    this.changeOrderService
      .createChangeOrder(formData, this.idempotencyKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeModal.close();
        },
        error: (error: unknown) => {
          this.loading.set(false);
          const detail = this.extractErrorDetail(error);
          this.errorMessage.set(`Failed to save change order. ${detail ?? 'Please try again.'}`);
        },
      });
  }

  cancel(): void {
    this.activeModal.dismiss(false);
  }

  private extractErrorDetail(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') return undefined;

    const sdkResponse = (error as { response?: unknown }).response;
    if (typeof sdkResponse === 'string' && sdkResponse.length > 0) {
      try {
        const parsed = JSON.parse(sdkResponse) as { detail?: unknown };
        if (typeof parsed.detail === 'string' && parsed.detail.length > 0) return parsed.detail;
      } catch {
        // not JSON, fall through
      }
    }

    const httpDetail = (error as { error?: { detail?: unknown } }).error?.detail;
    return typeof httpDetail === 'string' && httpDetail.length > 0 ? httpDetail : undefined;
  }
}
